/* eslint-disable no-use-before-define */
import { useEffect, useContext, useState, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Button, RadioGroup } from 'uxd-design-system-components';
import { useLocation } from 'react-router-dom';
import Spinner from 'react-bootstrap/Spinner';
import getText from '../../../assets/locale/locale';
import LocaleContext from '../../../contexts/LocaleContext';
import PlanningTopicsCard from '../../../components/PlanningTopicsCard/PlanningTopicsCard';
import PlanningTopicsRow from '../../../components/PlanningTopicsRow/PlanningTopicsRow';
import { planningTopicsMap } from '../../../constants/planningTopics';
import addDataLayerTrigger from '../../../utils/addDataLayerTrigger/addDataLayerTrigger';
import './FPPlanningTopicsPage.scss';
import FPPlanningTopicsPageProps from '../../../types/pages/FinancialPlanning/FPPlanningTopicsPageProps';
import PlanningTopic from '../../../types/PlanningTopic';
import InvestmentDecisionList from '../../../constants/InvestmentDecisionList';
import FinancialPlanningContent from '../../../types/pages/FinancialPlanning/FinancialPlanningContent';
import { usePreviousPath } from '../../../contexts/PreviousPathContext';
import { useDataLayerInfo } from '../../../contexts/DataLayerContext';

const PlanningTopicsPage = (props: FPPlanningTopicsPageProps) => {
  const { isLoading, planningTopicsBtnCallback, updateFinancialPlanningContent, financialPlanningContent } =
    props;
  const locId = useContext(LocaleContext);
  const SPACE = ' ';
  const ENTER = 'Enter';

  const [isContinueButtonDisabled, setIsContinueButtonDisabled] =
    useState(true);
  const [planningTopics, setPlanningTopics] = useState<PlanningTopic[]>([]);
  const [isMobile, setIsMobile] = useState(false);

  const maximumPhoneWidth = 768;
  const initRadioOptions = useMemo(() => [
    {
      checked: false,
      disabled: false,
      id: 'id-only-advisor',
      label: getText(locId, 'advisorOnlyOption', 'planningTopicsPage'),
      value: InvestmentDecisionList.ADVISOR_ONLY.code,
    },
    {
      checked: false,
      disabled: false,
      id: 'id-with-advisor',
      label: getText(locId, 'togetherWithAdvisorOption', 'planningTopicsPage'),
      value: InvestmentDecisionList.TOGETHER_WITH_ADVISOR.code,
    },
    {
      checked: false,
      disabled: false,
      id: 'id-myself-only',
      label: getText(locId, 'myselfOnlyOption', 'planningTopicsPage'),
      value: InvestmentDecisionList.MYSELF_ONLY.code,
    },
    {
      checked: false,
      disabled: false,
      id: 'id-it-depends',
      label: getText(locId, 'itDependsOption', 'planningTopicsPage'),
      value: InvestmentDecisionList.IT_DEPENDS.code,
    },
    {
      checked: false,
      disabled: false,
      id: 'id-unsure',
      label: getText(locId, 'unsureOption', 'planningTopicsPage'),
      value: InvestmentDecisionList.UNSURE.code,
    },
  ], [locId]);


  const [radioOptions, setRadioOptions] = useState(() => {
    if (financialPlanningContent?.investorInvolvementCode) {
      return initRadioOptions.map(option => ({
        ...option,
        checked: option.value === financialPlanningContent.investorInvolvementCode,
      }));
    }
    return initRadioOptions;
  });

  const [liveRegionMessage, setLiveRegionMessage] = useState<string>('');
  const [selectedInvestmentDecision, setSelectedInvestmentDecision] =
    useState<string>('');

  const checkboxRefs = useRef<{ [key: string]: HTMLInputElement | null }>({});
  const [focusTopicId, setFocusTopicId] = useState<string | null>(null);

  const updateInvestmentDecision = (optionValue: string) => {
    const updatedOptions = initRadioOptions.map(option => ({
        ...option,
        checked: option.value === optionValue,
    }));

    setSelectedInvestmentDecision(optionValue);
    setRadioOptions(updatedOptions);

    const investmentDecisionOptionLabel = updatedOptions.find(
      (investmentDecisionOption) =>
        investmentDecisionOption.value === optionValue
    )?.label;
    addDataLayerTrigger('radial_button_selection', {
      button_name: investmentDecisionOptionLabel,
    });
  };

  const handleMobileView = () =>
    setIsMobile(window.innerWidth < maximumPhoneWidth);

  const { setPreviousPath, previousPath } = usePreviousPath();
  const currentLocation = useLocation();
  const pageTitle = getText(locId, 'pageTitle', 'planningTopicsPage');
  const pageReferrer = previousPath
    ? `${window.location.origin}${previousPath}${currentLocation.search}`
    : undefined;
  const pageURL = `${window.location.origin}${currentLocation.pathname}${currentLocation.search}`;
  const virtualPageInfo = {
    pagePath: currentLocation.pathname,
    pageTitle,
    page_referrer: pageReferrer,
    external_referrer:
      window.document.referrer && window.document.referrer.trim() !== ''
        ? window.document.referrer
        : undefined,
    page_url: pageURL,
  };

  const { dataLayerInfo } = useDataLayerInfo();
  const updatesFPPlanningTopics = {
    ...dataLayerInfo,
    page_type: 'planning-topics-page',
  };

  useEffect(() => {
    document.title = pageTitle;
    setPreviousPath(currentLocation.pathname);
    addDataLayerTrigger('dataLayer-initialized', updatesFPPlanningTopics);
    addDataLayerTrigger('VirtualPageview', virtualPageInfo);
    handleMobileView();
    window.addEventListener('resize', handleMobileView);

    const topics: PlanningTopic[] = [];
    const investorInvolvementCode = financialPlanningContent?.investorInvolvementCode || '';
    planningTopicsMap.forEach((topic) => {
      const isChecked = financialPlanningContent?.planningTopics?.some(
        (t) => t.topicTitleId === topic.topicTitleId && t.checked
      );
      topics.push({ ...topic, checked: !!isChecked });
    });
    setPlanningTopics(topics);
    setSelectedInvestmentDecision(investorInvolvementCode);
    return () => {
      window.removeEventListener('resize', handleMobileView);
    };
  }, []);


  const handleCardClick = (topicId: string) => {
    const planningTopicCardTitle = planningTopicsMap.get(topicId)?.topicTitle;
    addDataLayerTrigger('card_click', { button_name: planningTopicCardTitle });
    setPlanningTopics(
      planningTopics.map((topic) =>
        topic.topicTitleId === topicId
          ? { ...topic, checked: !topic.checked }
          : topic
      )
    );
    setFocusTopicId(topicId);

    // Update live region message for screen readers
    const toggledTopic = planningTopics.find(t => t.topicTitleId === topicId);
    const isNowChecked = toggledTopic ? !toggledTopic.checked : false;
    setLiveRegionMessage(
      `${planningTopicCardTitle} ${isNowChecked ? 'checked' : 'unchecked'}`
    );

    // Focus the checkbox after state update
    setTimeout(() => {
      checkboxRefs.current[topicId]?.focus();
    }, 0);
  };
  // Focus on checkbox after DOM update
  // This is necessary to ensure the checkbox is focused after the state update for screen readers
  useEffect(() => {
    if (focusTopicId && checkboxRefs.current[focusTopicId]) {
      checkboxRefs.current[focusTopicId]?.focus();
      setFocusTopicId(null); // reset the flag
    }
  }, [planningTopics, focusTopicId]);

  const mapPlanningTopicsToCards = () => {
    return planningTopics.map((topic) => {
      return topic ? (
        <label htmlFor={`checkbox-${topic.topicTitleId}`} key={`planningTopicsCard-${topic.topicTitleId}`}>
          <div
            className="FPPlanningTopicsPage_card"
            data-testid={`planningTopicsCardContainer-${topic.topicTitleId}`}
            onKeyDown={(e) => {
              e.stopPropagation();
              if (e.key === ENTER || e.key === SPACE) {
                e.preventDefault();
                handleCardClick(topic.topicTitleId);
              }
            }}
          >
            <PlanningTopicsCard
              topicTitle={topic.topicTitle}
              topicStatement={topic.topicStatement}
              topicTitleId={topic.topicTitleId}
              checked={topic.checked}
              color={topic.color}
              handleCardClick={handleCardClick}
              ref={el => { checkboxRefs.current[topic.topicTitleId] = el; }}
            />
          </div>
        </label>
      ) : null;
    });
  };

  const mapPlanningTopicsToRows = () => {
    return planningTopics.map((topic) => {
      return topic ? (
        <label htmlFor={`checkbox-${topic.topicTitleId}`} className="FPPlanningTopicsPage_rowLabels"
          key={`planningTopicsRow-${topic.topicTitleId}`}>
          <div
            className="FPPlanningTopicsPage_row"
            data-testid={`planningTopicsRowContainer-${topic.topicTitleId}`}
            onKeyDown={(e) => {
              e.stopPropagation();
              if (e.key === ENTER || e.key === SPACE) {
                e.preventDefault();
                handleCardClick(topic.topicTitleId);
              }
            }}
          >
            <PlanningTopicsRow
              topicTitle={topic.topicTitle}
              topicStatement={topic.topicStatement}
              topicTitleId={topic.topicTitleId}
              checked={topic.checked}
              color={topic.color}
              handleCardClick={handleCardClick}
              ref={el => { checkboxRefs.current[topic.topicTitleId] = el; }}
            />
          </div>
        </label>
      ) : null;
    });
  };

  useEffect(() => {
    const hasSelectedTopic = planningTopics.some((topic) => topic.checked);
    const hasSelectedRadio = selectedInvestmentDecision !== '';

    setIsContinueButtonDisabled(
      !(hasSelectedTopic && hasSelectedRadio)
    );
  }, [planningTopics, selectedInvestmentDecision]);

  const saveFinancialPlanningContent = () => {
    const selectedTopics = planningTopics.filter((topic) => topic.checked);
    const planningTopicsData: FinancialPlanningContent = {
      planningTopics: selectedTopics,
      investorInvolvementCode: selectedInvestmentDecision,
    };
    addDataLayerTrigger('save_and_continue', {});
    planningTopicsBtnCallback();
    updateFinancialPlanningContent(planningTopicsData);
  };

  return (
    <div className="FPPlanningTopicsPage" data-testid="planning-topics-page">
      <div className="FPPlanningTopicsPage_content">
        {/* ARIA live region for screen readers */}
        <span
          aria-live="polite"
          aria-relevant="text"
          aria-atomic="true"
          className="FPPlanningTopicsPage_visually-hidden"
        >
          {liveRegionMessage}
        </span>
        <h1 className="heading--semi-bold">
          {getText(locId, 'header', 'planningTopicsPage')}
        </h1>
        {isMobile ? (
          <fieldset className="FPPlanningTopicsPage_rowsFieldset">
            <div className="FPPlanningTopicsPage_description">
              <legend>
                {getText(locId, 'description', 'planningTopicsPage')}
              </legend>
            </div>
            <div className="FPPlanningTopicsPage_rowsContainer">
              {isLoading ? (
                <div id="spinner" data-testid="planning-topics-spinner">
                  <Spinner animation="border" />
                </div>
              ) : (
                mapPlanningTopicsToRows()
              )}
            </div>
          </fieldset>
        ) : (
          <fieldset className="FPPlanningTopicsPage_cardsContainer">
            <div className="FPPlanningTopicsPage_description">
              <legend>
                {getText(locId, 'description', 'planningTopicsPage')}
              </legend>
              <div className="image-frame">
                <img
                  src="/public/images/welcome_image.png"
                  alt=""
                  role="presentation"
                />
              </div>
            </div>
            {isLoading ? (
              <div id="spinner" data-testid="planning-topics-spinner">
                <Spinner animation="border" />
              </div>
            ) : (
              mapPlanningTopicsToCards()
            )}
          </fieldset>
        )}
        <fieldset data-testid="planning-topics-investment-decision">
          <RadioGroup
            classes={['decision-group']}
            legend={getText(locId, 'selectInvestmentDecision', 'planningTopicsPage')}
            name="decision-group"
            onChange={updateInvestmentDecision}
            options={radioOptions}
          />
        </fieldset>

        <div className="FPPlanningTopicsPage_btnContainer">
          <Button
            label={getText(locId, 'saveBtnLabel', 'planningTopicsPage')}
            styleVariant="primary"
            onClick={saveFinancialPlanningContent}
            data-testid="FPPlanningTopicsPage_btn"
            attributes={{
              value: '',
              id: 'results-page-button_fp',
            }}
            disabled={isContinueButtonDisabled}
          />
        </div>

        <div className="footerFP">
          <div className="disclaimerFP">
            <div className="microcopy--2">
              {getText(locId, 'planningTopicsPageDisclosure', 'planningTopicsPage')}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

PlanningTopicsPage.propTypes = {
  planningTopicsBtnCallback: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
  updateFinancialPlanningContent: PropTypes.func.isRequired,
  financialPlanningContent: PropTypes.shape({
      // eslint-disable-next-line react/forbid-prop-types
      planningTopics: PropTypes.arrayOf(PropTypes.object),
      investorInvolvementCode: PropTypes.string,
    }),
};

export default PlanningTopicsPage;
